import { IPSubnetField } from '@ipfabric/ui-components/IPSubnetField/IPSubnetField';
import type {
  IPSubnetInputValue,
  IPSubnetInputValueWithError,
} from '@ipfabric/ui-components/IPSubnetInput/IPSubnetInput.types';
import {
  isIPv4,
  isIPv6,
} from '@ipfabric/ui-components/IPSubnetInput/helpers/ipAddressValidations';
import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import type { LoadingState } from '@ipfabric/ui-components/utils/loading/withLoadingIndicator';
import VendorsApi from 'app/features/DiscoverySettingsForms/components/VendorsApi/VendorsApi';
import type { ExistingVendor } from 'app/features/DiscoverySettingsForms/components/VendorsApi/vendorTypes';
import { FormikCheckboxField } from 'components/FormikCheckboxField/FormikCheckboxField';
import { NoCredentialsBox } from 'components/NoCredentialsBox/NoCredentialsBox';
import { useLocale } from 'containers/App/LocaleContext';
import { LoaderAndText } from 'containers/LoaderAndText/LoaderAndText';
import type { FormikProps } from 'formik';
import {
  type Dispatch,
  type FC,
  type SetStateAction,
  memo,
  useEffect,
  useState,
} from 'react';
import { useSnapshotId } from 'utils/hooks/useSnapshotId';

interface Props {
  insertNetworkFormik: FormikProps<InsertNetworkFormValues>;
  loadingVendorsState: LoadingState;
  onFetchVendors: () => void;
  vendors: ExistingVendor[];
  setVendors: Dispatch<SetStateAction<ExistingVendor[]>>;
}

export interface InsertNetworkFormValues {
  ipList: IPSubnetInputValueWithError | IPSubnetInputValue;
  retryTimedOut: boolean;
}

const calculateIpCount = (mask: number, isIPv6: boolean): number => {
  return isIPv6 ? 2 ** (128 - mask) : 2 ** (32 - mask);
};

const generateSubnetWarning = (
  ip: string,
  mask: number,
  isIPv6: boolean,
  msg: any,
): string => {
  const numberOfIpAddresses = calculateIpCount(mask, isIPv6);
  return msg.snapshotCreation.addDevices.warnings.largeSubnet
    .replace('{subnet}', `${ip}/${mask}`)
    .replace('{ipAddressCount}', numberOfIpAddresses.toString());
};

const BareSnapshotCreationAddDevices: FC<Props> = (props) => {
  const msg = useLocale();
  const snapshotId = useSnapshotId();
  const [warning, setWarning] = useState<string>('');

  const getEntryWarning = useStableCallback((ipAddresses: string[]): string => {
    if (!ipAddresses || ipAddresses.length === 0) {
      return '';
    }

    for (const entry of ipAddresses) {
      const [ip, maskAsString] = entry.split('/');
      // Fixed noUncheckedIndexedAccess NIM-19408
      const mask = Number.parseInt(maskAsString!, 10);

      if (isIPv4(ip) && !Number.isNaN(mask) && mask < 23) {
        // Fixed noUncheckedIndexedAccess NIM-19408
        return generateSubnetWarning(ip!, mask, false, msg);
      }

      if (isIPv6(ip) && !Number.isNaN(mask) && mask < 119) {
        // Fixed noUncheckedIndexedAccess NIM-19408
        return generateSubnetWarning(ip!, mask, true, msg);
      }
    }

    return '';
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: refetch on snapshot change
  useEffect(() => {
    props.onFetchVendors();
  }, [snapshotId]);

  const getNoCredentialsBox = () => {
    return (
      <NoCredentialsBox
        title={msg.discoverySettings.vendorsApi.noVendorApis}
        icon='fa-info-circle'
        type='info'
      />
    );
  };

  return (
    <div className='flex flex-col flex-1'>
      <div>
        <IPSubnetField
          name='ipList'
          value={props.insertNetworkFormik.values.ipList}
          error={(() => {
            return Array.isArray(props.insertNetworkFormik.errors.ipList)
              ? props.insertNetworkFormik.errors.ipList[0]
              : props.insertNetworkFormik.errors.ipList;
          })()}
          warning={warning}
          onChange={(
            value: IPSubnetInputValueWithError | IPSubnetInputValue,
          ) => {
            setWarning(getEntryWarning(value.ips));
            props.insertNetworkFormik.setFieldValue('ipList', value);
          }}
          placeholder='Insert one or more IPv4/IPv6 addresses or subnets'
          label='Device IP or Subnet'
        />
      </div>
      <div className='mb-5 mt-1.5 flex items-center'>
        <FormikCheckboxField
          formik={props.insertNetworkFormik}
          name='retryTimedOut'
          id='retryTimedOut'
        />
        <label htmlFor='retryTimedOut' className='pl-lgu-3 mb-0 mt-1'>
          {msg.snapshotCreation.addDevices.rediscoverDevices}
        </label>
      </div>

      {props.loadingVendorsState === 'LOADING' ? (
        <LoaderAndText />
      ) : (
        <div className='flex flex-col flex-1'>
          <VendorsApi
            newSnapshot={false}
            noCredentialBox={getNoCredentialsBox()}
            onVendorsChange={props.setVendors}
            showUpdateFromGlobalSettingsButton
            snapshotId={snapshotId}
            vendors={props.vendors ?? []}
          />
        </div>
      )}
    </div>
  );
};

export const SnapshotCreationAddDevices = memo(BareSnapshotCreationAddDevices);
